import dynamic from "next/dynamic";
import React from "react";
import { logError } from "repoV2/utils/common/error/error";
import { LISTING_TYPES } from "repoV2/constants/listing";
import { useRedirectIfConditionMet } from "repoV2/utils/common/render/useRedirectIfCondition";
import { APP_ROUTE_KEYS } from "repoV2/constants/urls&routing/routing";
import { constructRoute } from "repoV2/utils/urls&routing/routing";
import { PageBase } from "repoV2/components/common/PageBase/PageBase";
import { ICustomCodeData } from "repoV2/interfaces/customCodes/ICustomCode";
import { HtmlHeader } from "repoV2/components/common/HtmlHeader/HtmlHeader";
import { getFaviconUrl } from "repoV2/components/common/HtmlHeader/HtmlHeader.utils";
import { CustomCode } from "repoV2/components/customCode/CustomCode";
import { CUSTOM_CODES_POSITION } from "repoV2/constants/customCodes/customCodes.constants";
import { useIsMounted } from "repoV2/utils/common/render/mount";
import { callGetSalePageBasicDataApi } from "./utils/apis/getSalePageBasicDataApi/getSalePageBasicDataApi.api";
import { callGetSalePageTemplateDataApi } from "./utils/apis/getSalePageTemplateDataApi/getSalePageTemplateDataApi.api";
import { ISalePageArgs, ISalePageGetInitialPropsArgs } from "./ISalePage";
import { SalePageContextProvider } from "./SalePageContext";
import { ISalePageSalePageBasicDataFromApi } from "./utils/apis/getSalePageBasicDataApi/getSalePageBasicDataApi.interfaces";
import {
    IColorPaletteFromApi,
    ISalePageSectionsDataFromApi,
} from "./utils/apis/getSalePageTemplateDataApi/getSalePageTemplateDataApi.interfaces";
import { callGetRecordedContentCatalogueDataApi } from "./utils/apis/getRecordedContentCatalogueDataApi/getRecordedContentCatalogueDataApi.api";
import { IRecordedContentCatalogueDataFromApi } from "./utils/apis/getRecordedContentCatalogueDataApi/getRecordedContentCatalogueDataApi.interfaces";
import {
    DEFAULT_SECTIONS_COUNT_TO_LOAD_ON_SSR,
    TEMPLATE_SECTIONS_COUNT_TO_LOAD_ON_SSR,
} from "./utils/sale.constants";
import { TEMPLATE_SLUGS, ssr_screen_size_defaults } from "./constants";
import { CustomCodeSection } from "./components/CustomCodeSection";
import { getSalePageCustomCodes } from "./utils/apis/getSalePageCustomCodes";
import { checkSsrMobileSubdomainExists } from "./utils/common/getSsrMobileSubdomains";

const Template1 = dynamic(() => import("./templates/Template1/Template1"));
const Template2 = dynamic(() => import("./templates/Template2/Template2"));
const Template3 = dynamic(() => import("./templates/Template3/Template3"));
const Template4 = dynamic(() => import("./templates/Template4/Template4"));
const Template5 = dynamic(() => import("./templates/Template5/Template5"));
const Template6 = dynamic(() => import("./templates/Template6/Template6"));
const Template7 = dynamic(() => import("./templates/Template7/Template7"));
const Template9 = dynamic(() => import("./templates/Template9/Template9"));
const Template10 = dynamic(() => import("./templates/Template10/Template10"));
const Template11 = dynamic(() => import("./templates/Template11/Template11"));

const SalePage = ({
    hostName = "",
    basicData,
    colorPalette,
    recordedContentCatalogueItemsList,
    sectionsData,
    urlQueryParams,
    customCodes,
    abTesting,
}: ISalePageArgs) => {
    const shouldRedirectAway = !sectionsData;
    useRedirectIfConditionMet({
        condition: shouldRedirectAway,
        url: constructRoute({
            appRouteKey: APP_ROUTE_KEYS.HOME,
        }),
    });

    const isMounted = useIsMounted();

    if (shouldRedirectAway) {
        return null;
    }

    const {
        preview,
        template: templateParam,
        disable_animation: disableAnimation,
    } = urlQueryParams;
    const templateSlug = templateParam || basicData?.templateSlug;
    let TemplateComponent;
    /**
     * only make this variable true for those templates which have updated code to support SSR
     */
    let doesTemplateSupportSSR = false;
    switch (templateSlug) {
        case TEMPLATE_SLUGS.template2:
            TemplateComponent = Template2;
            break;
        case TEMPLATE_SLUGS.template3:
            TemplateComponent = Template3;
            // template 3 has SRR support by relying completely on media queries for responsiveness
            doesTemplateSupportSSR = true;
            break;
        case TEMPLATE_SLUGS.template4:
            TemplateComponent = Template4;
            // template 4 has SRR support by relying completely on media queries for responsiveness
            doesTemplateSupportSSR = true;
            break;
        case TEMPLATE_SLUGS.template5:
            TemplateComponent = Template5;
            break;
        case TEMPLATE_SLUGS.template6:
            TemplateComponent = Template6;
            // template 6 has SRR support by relying completely on media queries for responsiveness
            doesTemplateSupportSSR = true;
            break;
        case TEMPLATE_SLUGS.template7:
            TemplateComponent = Template7;
            // template 7 has SRR support by relying completely on media queries for responsiveness
            doesTemplateSupportSSR = true;
            break;
        case TEMPLATE_SLUGS.template9:
            TemplateComponent = Template9;
            break;
        case TEMPLATE_SLUGS.template10:
            TemplateComponent = Template10;
            break;
        case TEMPLATE_SLUGS.template11:
            TemplateComponent = Template11;
            break;
        case TEMPLATE_SLUGS.template1:
        default:
            TemplateComponent = Template1;
            // template 1 has SRR support by relying completely on media queries for responsiveness
            doesTemplateSupportSSR = true;
            break;
    }

    // if: subdomain is an internal domain for SSR page speed testing
    //     OR if template supports SSR
    // then: we need to render the page on server side
    // else: we return null on server side and actually render on client side
    if (!(checkSsrMobileSubdomainExists(hostName) || doesTemplateSupportSSR)) {
        if (!isMounted) return null;
    }

    const finalComponent = (
        <PageBase hostName={hostName}>
            <HtmlHeader
                title={
                    basicData?.seoPreview?.title ||
                    sectionsData.tabAssets.title ||
                    basicData?.listingTitle ||
                    ""
                }
                favicon={getFaviconUrl({
                    pageSpecificFavIcon: sectionsData.tabAssets.favIcon,
                    creatorSiteFaviconImgSrc:
                        basicData?.aboutCreator?.creatorSiteFaviconImgSrc,
                    profileImgSrc: basicData?.aboutCreator?.profileImgSrc,
                    hostName,
                })}
                seoPreview={basicData?.seoPreview || {}}
            >
                {CustomCode({
                    position: CUSTOM_CODES_POSITION.HEAD,
                    codes: customCodes || [],
                })}
            </HtmlHeader>
            <SalePageContextProvider
                basicData={basicData}
                preview={preview}
                disableAnimation={disableAnimation}
                recordedContentCatalogueItemsList={
                    recordedContentCatalogueItemsList
                }
                colorPalette={colorPalette}
                sectionsData={sectionsData}
                abTesting={abTesting || {}}
            >
                <CustomCodeSection
                    position={CUSTOM_CODES_POSITION.MAIN_CONTENT_START}
                    codes={customCodes || []}
                />
                <TemplateComponent />
                <CustomCodeSection
                    position={CUSTOM_CODES_POSITION.MAIN_CONTENT_END}
                    codes={customCodes || []}
                />
            </SalePageContextProvider>
        </PageBase>
    );
    return finalComponent;
};

SalePage.getInitialProps = async (
    ctx: ISalePageGetInitialPropsArgs
): Promise<ISalePageArgs> => {
    const { hostName } = ctx.params;
    const { slug } = ctx.query;
    const urlQueryParams = {
        preview: !!ctx.query.preview,
        template: ctx.query.template ? `${ctx.query.template}` : "",
        disable_animation: !!ctx.query.disable_animation,
    };

    if (!slug) return { urlQueryParams };

    let basicData: ISalePageSalePageBasicDataFromApi | undefined;
    try {
        basicData = await callGetSalePageBasicDataApi({
            salePageUuid: `${slug}`,
            subDomain: hostName,
        });
    } catch (error) {
        logError({
            error,
            occuredAt: "SalesPage.getInitialProps",
            when: "callGetSalePageDetailsApi",
        });
    }

    let recordedContentCatalogueItemsList:
        | IRecordedContentCatalogueDataFromApi
        | undefined;
    try {
        if (basicData?.listingType === LISTING_TYPES.RECORDED_CONTENT) {
            recordedContentCatalogueItemsList =
                await callGetRecordedContentCatalogueDataApi({
                    listingUuid: basicData?.listingUuid,
                });
        }
    } catch (error) {
        logError({
            error,
            occuredAt: "SalesPage.getInitialProps",
            when: "callGetRecordedContentCatalogueDataApi",
        });
    }

    let sectionsData: ISalePageSectionsDataFromApi | undefined;
    let colorPalette: IColorPaletteFromApi | undefined;
    let customCodes: ICustomCodeData[] | undefined;
    try {
        const salePageTemplateData = await callGetSalePageTemplateDataApi({
            salePageUuid: `${basicData?.uuid}`,
            startRange: DEFAULT_SECTIONS_COUNT_TO_LOAD_ON_SSR.START_RANGE,
            endRange: (
                TEMPLATE_SECTIONS_COUNT_TO_LOAD_ON_SSR[
                    basicData?.templateSlug || ""
                ] || DEFAULT_SECTIONS_COUNT_TO_LOAD_ON_SSR
            ).END_RANGE,

            recordedContentCatalogueItemsList,
        });
        sectionsData = salePageTemplateData.sectionsData;

        colorPalette = salePageTemplateData.colorPalette;
    } catch (error) {
        logError({
            error,
            occuredAt: "SalesPage.getInitialProps",
            when: "callGetSalePageSectionsDataApi",
        });
    }

    try {
        const _customCodes = await getSalePageCustomCodes({
            salePageUuid: `${slug}`,
            subDomain: hostName,
        });
        customCodes = _customCodes;
    } catch (error) {
        logError({
            error,
            occuredAt: "SalesPage.getInitialProps",
            when: "getSalePageCustomCodes",
        });
    }

    const ssrScreenSizeDefault = checkSsrMobileSubdomainExists(hostName)
        ? ssr_screen_size_defaults.mobile
        : undefined;

    const returnVal: ISalePageArgs = {
        hostName,
        basicData,
        sectionsData,
        recordedContentCatalogueItemsList,
        colorPalette,
        urlQueryParams,
        customCodes,
        abTesting: { ssrScreenSizeDefault },
    };
    return returnVal;
};

export default SalePage;
